import React, { createContext, useContext, useState, ReactNode } from 'react';
import { SEASONS, CURRENT_SEASON, CURRENT_SEASON_KEY, getSeason } from './seasons';

export interface SeasonConfig {
  key: string;
  label: string;
  divisionName: string;
  chipLabel: string;
  seasonId: string;
  divisionId: string;
  totalTeams: number;
  startSchedule: string;
  endSchedule: string;
  teamName: string;
  teamId?: string;
  isCurrent?: boolean;
  volunteerSheetUrl?: string;
}

interface SeasonContextType {
  currentSeason: SeasonConfig;
  selectedSeason: SeasonConfig;
  setSelectedSeasonKey: (key: string) => void;
  isViewingPastSeason: boolean;
  seasons: SeasonConfig[];
}

const SeasonContext = createContext<SeasonContextType | undefined>(undefined);

export const SeasonProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedSeasonKey, setSelectedSeasonKeyState] = useState<string>(() => {
    const saved = localStorage.getItem('selectedSeasonKey');
    if (saved && SEASONS.some(s => s.key === saved)) {
      return saved;
    }
    return CURRENT_SEASON_KEY;
  });

  const setSelectedSeasonKey = (key: string) => {
    if (SEASONS.some(s => s.key === key)) {
      setSelectedSeasonKeyState(key);
      localStorage.setItem('selectedSeasonKey', key);
    }
  };

  const selectedSeason = (getSeason(selectedSeasonKey) as SeasonConfig) || (CURRENT_SEASON as SeasonConfig);
  const isViewingPastSeason = selectedSeason.key !== CURRENT_SEASON_KEY;

  return (
    <SeasonContext.Provider
      value={{
        currentSeason: CURRENT_SEASON as SeasonConfig,
        selectedSeason,
        setSelectedSeasonKey,
        isViewingPastSeason,
        seasons: SEASONS as SeasonConfig[],
      }}
    >
      {children}
    </SeasonContext.Provider>
  );
};

export const useSeason = (): SeasonContextType => {
  const context = useContext(SeasonContext);
  if (!context) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  return context;
};
